import { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSupportSession } from '../../hooks/useSupportSession';
import { useMySupportGrants } from '../../api/hooks';
import { ROLE_LABELS } from '../team/roleLabels';
import { darkTheme, FONT } from '../../theme/tokens';
import { formatExpiryClock, formatRemaining, useNow } from './supportTime';

const t = darkTheme;

/**
 * The staff support-mode strip (SC-2948). While the session is inside a client
 * org under a support grant it is a full-width, non-dismissable amber bar —
 * which org, what role, when it expires, and Exit — because every action taken
 * here is audited as support on the customer's workspace and that must never be
 * forgettable. Otherwise it shows any pending support notice (couldn't enter, the
 * grant ended mid-session), dismissable. Renders nothing for everyone else.
 */
export function SupportModeBanner() {
  const { support, notice, dismissNotice, exitSupport } = useSupportSession();

  if (support) {
    return (
      <>
        <ActiveSupportBar
          organizationName={support.organizationName}
          organizationId={support.organizationId}
          role={support.role}
          expiresAt={support.expiresAt}
          onExit={() => { void exitSupport(); }}
        />
        {notice && <NoticeBar tone={notice.tone} message={notice.message} onDismiss={dismissNotice} />}
      </>
    );
  }
  if (notice) return <NoticeBar tone={notice.tone} message={notice.message} onDismiss={dismissNotice} />;
  return null;
}

function ActiveSupportBar({
  organizationName,
  organizationId,
  role,
  expiresAt,
  onExit,
}: {
  organizationName: string;
  organizationId: string;
  role: keyof typeof ROLE_LABELS;
  expiresAt: string;
  onExit: () => void;
}) {
  const now = useNow();
  const { user } = useAuth();
  const { syncSupportExpiry } = useSupportSession();
  // Re-read the grant so a colleague's re-grant (which replaces the grant in
  // place, usually extending it) moves the expiry here and in the timer.
  const { data } = useMySupportGrants(user?.id, true);
  const liveGrant = data?.grants.find((g) => g.organization.id === organizationId);
  useEffect(() => {
    if (liveGrant) syncSupportExpiry(liveGrant);
  }, [liveGrant, syncSupportExpiry]);

  return (
    <div
      role="region"
      aria-label="Support access"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '8px 16px', background: t.amber, color: t.accentButtonText,
        fontFamily: FONT, fontSize: 13, flexShrink: 0,
      }}
    >
      <span style={{ fontWeight: 700, letterSpacing: '0.04em', fontSize: 11 }}>SUPPORT ACCESS</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        You&apos;re in <strong>{organizationName}</strong> as {ROLE_LABELS[role] ?? role}
        {' · '}expires {formatExpiryClock(expiresAt, now)} ({formatRemaining(expiresAt, now)} left)
      </span>
      <button
        type="button"
        onClick={onExit}
        style={{
          padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
          fontFamily: FONT, cursor: 'pointer',
          background: t.accentButtonText, color: t.amber, border: 'none',
        }}
      >
        Exit {organizationName}
      </button>
    </div>
  );
}

function NoticeBar({
  tone,
  message,
  onDismiss,
}: {
  tone: 'info' | 'error';
  message: string;
  onDismiss: () => void;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 16px', fontFamily: FONT, fontSize: 13, flexShrink: 0,
        background: tone === 'error' ? t.redBg : t.amberBgSubtle,
        color: tone === 'error' ? t.redText : t.amberText,
        borderBottom: `1px solid ${tone === 'error' ? t.redBorder : t.amber}`,
      }}
    >
      <span style={{ flex: 1 }}>{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent', border: 'none', color: 'inherit',
          cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4,
        }}
      >
        ×
      </button>
    </div>
  );
}
